import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useModal, usePage, useTheme } from "@/comn/hooks";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import {
    APIS,
    SF_OGA_USR_INFO,
    SG_USR_AUTH_HIST_LIST,
    SG_USR_AUTH_LIST,
    URLS,
} from "@/tra/tancis/ptli/ptl/orgauth/services/PtliOgaAuthService";
import { PTLI1008003Q } from "@/tra/tancis/ptli/ptl/orgauth/PTLI1008003Q";

/*
 * Save. OGA User Auth Data
 * !== 세관 사용자 권한 저장. ==!
 */
export const PTLI1008002S = (): JSX.Element => {
    const modal = useModal();
    const param = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });

    const form = { ogaUsrInfo: useForm({ defaultSchema: SF_OGA_USR_INFO }) };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
        ogaUsrAuthHistList: useGrid({ defaultSchema: SG_USR_AUTH_HIST_LIST }),
    };

    const fetch = {
        getOgaUsrDtl: useFetch({
            api: () => APIS.getOgaUsrDtl({ tin: param["tin"] }),
            onSuccess: (data: any) => {
                const { content } = data.ogaUsrDtl;
                form.ogaUsrInfo.setValues(content);
                fetch.getOgaUsrAuthList.fetch();
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        getOgaUsrAuthList: useFetch({
            api: () => APIS.getOgaUsrAuthList({ tin: param["tin"] }),
            onSuccess: () => {
                fetch.getOgaUsrAuthHistList.fetch(param["tin"]);
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        getOgaUsrAuthHistList: useFetch({
            api: (data: any) => APIS.getOgaUsrAuthHistList(data),
            onSuccess: () => {},
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        saveOgaUsrAuthList: useFetch({
            api: () =>
                APIS.saveOgaUsrAuthList({
                    tin: param["tin"],
                    usrAuthList: grid.ogaUsrAuthList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00062" }); // 정상적으로 저장되었습니다.
                fetch.getOgaUsrDtl.fetch();
            },
            onError: () => modal.openModal({ content: "msg.com.00026" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };

    const handler = {
        onBtnSaveClick: () => {
            const list = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
            if (list.length === 0) {
                modal.openModal({ content: "msg.ptl.00741" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveOgaUsrAuthList.fetch(),
            });
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.ptl.00745" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <PTLI1008003Q
                        tin={param["tin"]}
                        ogaUsrAuthList={grid.ogaUsrAuthList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.ogaUsrAuthList.deleteRow("checkbox");
                },
            });
        },
    };

    useEffect(() => {
        fetch.getOgaUsrDtl.fetch();
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.ogaNm} />
                            <Group.Control {...form.ogaUsrInfo.schema.ogaDeptNm} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.usrMoblNo} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.ogaTelno} />
                            <Group.Control {...form.ogaUsrInfo.schema.ogaFaxNo} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.ogaUsrAuthList.grid} data={fetch.getOgaUsrAuthList.data?.ogaUsrAuthList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_HIST_LIST"} />
                    <Group.Section gap={2}>
                        <Grid
                            {...grid.ogaUsrAuthHistList.grid}
                            data={fetch.getOgaUsrAuthHistList.data?.ogaUsrAuthHistList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Button role={"list"} onClick={() => navigate(`${URLS.ptli1008001q}`)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role={"save"} onClick={handler.onBtnSaveClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
